import React, { useEffect, useState, useCallback } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExport, faSync, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import API_BASE_URL from "../../config";
import './RouterLogs.css';

const RouterLog = ({ onBack }) => {
    const [logs, setLogs] = useState([]);
    const [visibleCount, setVisibleCount] = useState(50);
    const [loading, setLoading] = useState(false);
    const [selectedRouter, setSelectedRouter] = useState("all");
    const [routerList, setRouterList] = useState([]);

    const getLogStatusClass = (text) => {
        const logText = (text || "").toLowerCase();
        if (logText.includes("connected") || logText.includes("up") || logText.includes("established")) return "rt-status-online";
        if (logText.includes("disconnected") || logText.includes("down") || logText.includes("failed")) return "rt-status-offline";
        return "rt-status-default";
    };

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const endpoint = selectedRouter === "all" ? "" : `/${selectedRouter}`;
            // Corrected endpoint using API_BASE_URL
            const response = await fetch(`${API_BASE_URL}/log-monitor/router${endpoint}`);
            const data = await response.json();
            
            const validData = Array.isArray(data) ? data : [];
            setLogs(validData);
            
            if (selectedRouter === "all" && validData.length > 0) {
                const unique = [...new Set(validData.map(log => log.hostname).filter(Boolean))].sort();
                setRouterList(unique);
            }
        } catch (err) { 
            console.error("Router log fetch error:", err); 
            setLogs([]);
        } finally { 
            setLoading(false); 
        }
    }, [selectedRouter]);

    useEffect(() => {
        fetchLogs();
        const interval = setInterval(fetchLogs, 15000);
        return () => clearInterval(interval);
    }, [fetchLogs]);

    const exportLogs = () => {
        if (logs.length === 0) return;
        const currentLogs = logs.slice(0, visibleCount);
        const csvContent = ["Hostname,Time,Message", ...currentLogs.map(log => `"${log.hostname || ''}","${log.time || ''}","${(log.message || '').replace(/"/g, '""')}"`)].join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `Router_Logs_${selectedRouter}.csv`;
        link.click();
    };

    return (
        <div className="rt-main-container">
            <div className="rt-header-wrapper">
                <div className="rt-header-inner">
                    <button className="rt-back-btn" onClick={onBack}>
                        <FontAwesomeIcon icon={faArrowLeft} /> Back
                    </button>
                    
                    <h2 className="rt-page-title">ROUTER LIVE LOGS</h2>
                    
                    <div className="rt-header-controls">
                        <select className="rt-dropdown" value={selectedRouter} onChange={(e) => { setSelectedRouter(e.target.value); setVisibleCount(50); }}>
                            <option value="all">ALL ROUTERS</option>
                            {routerList.map(name => <option key={name} value={name}>{name.toUpperCase()}</option>)}
                        </select>
                        <button className="rt-icon-btn refresh" title="Refresh" onClick={fetchLogs} disabled={loading}>
                            <FontAwesomeIcon icon={faSync} spin={loading} />
                        </button>
                        <button className="rt-icon-btn export" title="Export CSV" onClick={exportLogs} disabled={logs.length === 0}>
                            <FontAwesomeIcon icon={faFileExport} />
                        </button>
                    </div>
                </div>
            </div>

            <div className="rt-log-display-area">
                {!loading || logs.length > 0 ? (
                    <ul className="rt-log-ul">
                        {logs.slice(0, visibleCount).map((log, index) => (
                            <li key={log.id || index} className="rt-log-li">
                                <span className="rt-col-host">{log.hostname}</span> 
                                <span className="rt-col-time">[{log.time}]</span> 
                                <span className={`rt-col-text ${getLogStatusClass(log.message)}`}>
                                    {log.message}
                                </span>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className="rt-loader">Syncing Router Logs...</div>
                )}
                
                {logs.length > visibleCount && (
                    <div className="rt-load-more-container">
                        <button className="rt-load-more-btn" onClick={() => setVisibleCount(p => p + 50)}>
                            Load More Logs
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RouterLog;